<script lang="ts" setup>
import { onMounted, computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  Movements,
  getHistoric,
  getHistoricOld,
  getSearch
} from '../../../core/services/processed-payrolls'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronLeftDoubleIcon,
  ChevronRightDoubleIcon,
  DTextField,
  DBtn
} from 'front-library'
import DInfo from './DInfo.vue'
import { usePagination } from '@/app/core/composable/tables'
import { useI18n } from 'vue-i18n'
import { useNotificationsStore } from '@/store/notifications'

/**
 * router
 */
const router = useRouter()
const { t } = useI18n()
const notifications = useNotificationsStore()
export interface Historic {
  type: 'movements' | 'premovements' | string
  companyCode: string
  payrollCode: string
  alterCodesConcepts: boolean
  alterNamesConcepts: boolean
  process: string
  endDate: string
  processedPayrollId: string
}
const props = defineProps<{
  dataHistoric?: Historic
  employees: number
}>()
/**
 * strategias por tipo de ruta
 * Premovements usar lambda aws
 * movements usar microservicio del cluster
 */
const strategies = {
  movements: {
    getHistoric: getHistoricOld,
    getSearch: getSearch
  },
  premovements: {
    getHistoric: getHistoric,
    getSearch: getHistoric
  }
} as const
/**
 * Propiedades Computadas
 */
const convertNumber = computed(() => {
  return (number: any) =>
    Number(number).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
})

// elegir estrategia activa segun el param de la ruta "type"
const routeType = computed<'movements' | 'premovements'>(() => {
  const t = props.dataHistoric?.type?.toLowerCase() as
    | 'movements'
    | 'premovements'
    | undefined
  return t === 'premovements' ? 'premovements' : 'movements'
})

const svc = computed(() => strategies[routeType.value])

export interface PageHistoric extends Historic {
  offset: number
  size: number
}

const emit = defineEmits(['isError', 'loading'])

const [historicPage] = await Promise.all([
  svc.value.getHistoric(props.dataHistoric, 0, 10, true)
])

let movements: Movements = historicPage

const rowsPerPage = ref([10, 50, 100, 200])
const perPage = ref(10)
const searchText = ref('')
const haveLastPage = ref(false)
const allConsulted = ref(false)
const isSearching = ref(false)

const {
  currentPageRows,
  currentPage,
  totalPages,
  nextPage,
  updateRows,
  setPage,
  prevPage,
  goToFirsPage,
  goToLastPage,
  search,
  setConfig
} = usePagination<any>(movements.rows, props.employees)

const back = () => {
  router.go(-1)
}

const validateErrors = () => {
  const { errors, concepts } = movements.rows[0] ?? []

  let isError = false
  if (concepts?.length > 0) {
    for (const concept of concepts) {
      if (concept.error !== '') {
        isError = true
      }
    }
  }

  if ((errors?.length !== undefined && errors.length > 0) || isError) {
    return false
  } else {
    return true
  }
}

const executeGetPage = async () => {
  let offset = currentPage.value * perPage.value
  let newOffset = (currentPage.value + 1) * perPage.value

  if (
    offset == movements.rows.length ||
    (newOffset > movements.rows.length && !allConsulted.value)
  ) {
    let remaining = movements.rows.length % perPage.value
    let size = remaining == 0 ? perPage.value : perPage.value - remaining

    emitLoading(true)
    let page = await svc.value.getHistoric({
      ...props.dataHistoric,
      offset,
      size
    })
    emitLoading(false)

    if (!haveLastPage.value) {
      const oldPage = currentPage.value
      movements.rows.push(...page.rows)
      if (movements.rows.length == props.employees) {
        allConsulted.value = true
      }
      updateRows(movements.rows, props.employees)
      setPage(oldPage + 1)
      haveLastPage.value = currentPage.value == totalPages.value
    }
  } else {
    nextPage()
  }
}

const updatePerPage = async (perPage: number) => {
  if (perPage > movements.rows.length && isSearching.value === false) {
    emitLoading(true)
    let page = await svc.value.getHistoric({
      ...props.dataHistoric,
      offset: movements.rows.length,
      size: perPage - movements.rows.length
    })
    emitLoading(false)

    if (!haveLastPage.value) {
      movements.rows.push(...page.rows)
      updateRows(movements.rows, props.employees)
      setPage(1)
      haveLastPage.value = currentPage.value == totalPages.value
    }
  }
  setConfig(perPage)
}

const updateSearch = async (searchText: string) => {
  if (searchText === '') {
    emitLoading(true)
    let page = await svc.value.getHistoric(props.dataHistoric)
    emitLoading(false)

    movements.rows = page.rows
    updateRows(movements.rows, props.employees)
    setPage(1)
    haveLastPage.value = false
    isSearching.value = false
    return
  } else if (!allConsulted.value) {
    isSearching.value = true
    emitLoading(true)
    let page = await svc.value.getSearch({
      ...props.dataHistoric,
      search: searchText
    })
    emitLoading(false)
    if (!haveLastPage.value) {
      movements.rows = page.rows
      updateRows(movements.rows, movements.rows.length)
      setPage(1)
      haveLastPage.value = true
    }
  }

  search(searchText, allConsulted.value)
}

const emitError = (val: boolean, errorMovements: boolean) => {
  if (errorMovements) {
    notifications.addNotification({
      title: t('pendingProcesses.notification.sheetError'),
      type: 'error',
      time: 10000
    })
  }
  emit('isError', val)
}

const emitLoading = (val: boolean) => {
  emit('loading', val)
}

const hasAverage = (avg: unknown): boolean => {
  if (avg === null || avg === undefined) return false

  if (typeof avg === 'string') {
    const s = avg.trim()
    if (s === '' || s === '""' || s === '\\"\\"') return false

    try {
      const parsed = JSON.parse(s)
      return parsed && typeof parsed === 'object' && 'result' in parsed
    } catch {
      return true
    }
  }

  return false
}

const errorGetMovements = () => {
  return !movements?.rows || movements.rows.length === 0
}

onMounted(() => {
  const isErrors = validateErrors()
  const errorMovements = errorGetMovements()
  emitError(isErrors, errorMovements)
})
</script>
<template>
  <div class="h-full w-full">
    <div class="flex ml-1 py-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
      <DTextField
        :placeholder="t('form.button.search') + '...'"
        :icon="'SearchLgIcon'"
        class="mr-2 w-full"
        v-model="searchText"
        @keyup.enter="updateSearch(searchText)" />
      <DBtn
        @click.prevent="updateSearch(searchText)"
        v-html="t('form.button.search')" />
    </div>
    <div
      style="max-height: 45%"
      class="w-full border overflow-auto scroll-gray min-h-fit">
      <table class="w-full">
        <tfoot class="sticky z-30 bottom-0 w-48 h-12" style="bottom: -1px">
          <tr>
            <td
              class="sticky left-0 bg-gray-100 border-r-2 border-gray-300 overflow-hidden text-gray-600 px-5 text-end font-semibold">
              {{ t('payrollSheet.list.total') }}
            </td>
            <td
              class="px-3 bg-gray-100 text-right"
              v-for="footer in movements?.footer"
              :key="footer.conceptCode">
              <span class="font-bold text-blue-800 p-3">
                {{ convertNumber(footer.quantity) }}
              </span>
              {{ convertNumber(footer.amount) }}
            </td>
          </tr>
        </tfoot>
        <tbody class="bg-gray-50">
          <tr
            class="body-tr bg-white border-b-2 border-b-gray-100 h-14"
            v-for="row in currentPageRows"
            :key="row.employeeCode">
            <td
              class="sticky border-r-2 border-gray-300 left-0 bg-white hover:bg-state-100 w-48 px-2">
              <div class="flex flex-row items-center justify-between gap-3">
                <p>{{ row.employeeCode }} - {{ row.employeeName }}</p>
                <DInfo v-if="row.errors" :data="row" />
              </div>
            </td>
            <td
              class="px-3 border-b-2 border-gray-100 whitespace-nowrap"
              v-for="concept in row.concepts"
              :key="concept.conceptCode">
              <div class="flex flex-row justify-end gap-3">
                <span class="font-bold text-blue-800">{{
                  convertNumber(concept.quantity)
                }}</span>
                {{ convertNumber(concept.amount) }}
                <DInfo
                  v-if="
                    concept.error ||
                    concept.evalQuantity ||
                    concept.evalAmount ||
                    hasAverage(concept.average)
                  "
                  :data="concept" />
              </div>
            </td>
          </tr>
        </tbody>
        <thead class="sticky top-0 z-30 bg-white shadow-md h-14">
          <tr>
            <th
              id="employeeCode"
              class="sticky left-0 bg-white border-r-2 border-gray-300 text-gray-600 shadow-md h-14"
              style="min-width: 350px">
              {{ t('payrollSheet.list.codeName') }}
            </th>
            <th
              class="whitespace-nowrap px-3 text-gray-600"
              v-for="header in movements?.headers"
              :key="header.key"
              :id="header.key">
              {{ `${header.key} - ${header.value}` }}
            </th>
          </tr>
        </thead>
      </table>
    </div>
    <div
      class="flex justify-between px-3 items-center h-16 text-gray-500 bg-white rounded-b-md ml pr-4 border-gray-200 border-b-2 border-l-2 border-r-2">
      <div>
        <DBtn
          @click.prevent="back"
          color="white"
          v-html="t('payrollSheet.button.back')" />
      </div>
      <div class="flex gap-2">
        <div>
          <label for="" v-html="t('table.rowsPerPage')"></label>
          <select v-model="perPage" @change="updatePerPage(perPage)">
            <option v-for="(item, i) in rowsPerPage" :key="i" :value="item">
              {{ item }}
            </option>
          </select>
        </div>
        <span>
          {{
            t('payrollSheet.list.page', {
              currentPage: currentPage,
              totalPages: totalPages
            })
          }}
        </span>
        <div class="flex">
          <ChevronLeftDoubleIcon
            @click="goToFirsPage"
            :class="[
              currentPage == 1 ? 'text-gray-300' : 'text-gray-600',
              'cursor-pointer h-7 w-7'
            ]" />
          <ChevronLeftIcon
            @click="prevPage"
            :class="[
              currentPage == 1 ? 'text-gray-300' : 'text-gray-600',
              'cursor-pointer h-7 w-7'
            ]" />
          <ChevronRightIcon
            @click="currentPage != totalPages ? executeGetPage() : null"
            :class="[
              currentPage == totalPages
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 cursor-pointer',
              'h-7 w-7'
            ]" />
          <ChevronRightDoubleIcon
            @click="
              haveLastPage && currentPage != totalPages ? goToLastPage() : null
            "
            :class="[
              !haveLastPage || currentPage == totalPages
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 cursor-pointer',
              'h-7 w-7'
            ]" />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
:hover.body-tr > td {
  background-color: #f3f4f6;
}
.scroll-gray::-webkit-scrollbar {
  width: 10px;
  height: 10px;
  background: #f7f7f7;
}
.scroll-gray::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px rgb(177, 177, 177);
  border-radius: 5px;
}
.scroll-gray::-webkit-scrollbar-thumb {
  background: #cecece;
  border-radius: 5px;
}
.scroll-gray::-webkit-scrollbar-thumb:hover {
  background: #bbbbbb;
}
</style>
